import { useCallback, useMemo } from 'react';

import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  Query,
  QueryClient,
  useQueryClient,
  InfiniteData,
} from '@tanstack/react-query';
import { isBefore, parseISO } from 'date-fns';
import cloneDeep from 'lodash-es/cloneDeep';
import debounce, { type DebounceSettings, type DebouncedFunc } from 'lodash-es/debounce';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SetRequired, PartialDeep } from 'type-fest';

import { conversationsEndpoints as endpoints, messagesEndpoints } from '@/api/endpoints';
import { ApiError, callDelete, callGet, callPatch, callPut } from '@/api/fetcher';
import { invalidateQuery } from '@/hooks/queryCache/invalidateQuery';
import { createCowMerger, mergeCopyOnWrite } from '@/hooks/queryCache/mergeCopyOnWrite';
import { getNextCursor } from '@/hooks/queryCache/nextCursor';
import {
  appendItemToLastPage,
  prependItemToFirstPage,
  removeItemFromPages,
  updateItemInPages,
} from '@/hooks/queryCache/pagedCache';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import { commentKeys } from '@/modules/humanChat/hooks/useComments';
import { endpoints as eventsEndpoints } from '@/modules/humanChat/hooks/useConversationEvents';
import { invalidateScheduledMessages } from '@/modules/humanChat/hooks/useScheduledMessages';
import {
  Conversations,
  Conversation,
  ConversationUpdate,
  SessionActivity,
  ConversationStatusType,
  Sort,
} from '@/shared/types/chat';
import { ConversationAssignee, ConversationId, ConversationRouting } from '@/shared/types/rtm';

import { applyCountDelta, CountedState, CountScope } from './countDeltas';
import { invalidateCounts, isFetchingCounts } from '../hooks/useConversationsCounts';

// ['conversations', deskId, status, sort, agentId, brainId, departmentId, viewId]
export const conversationKeys = {
  base: (deskId: string) => ['conversations', deskId] as const,
  unassigned: (deskId: string, status: string, sort: string) =>
    [...conversationKeys.base(deskId), status, sort, 'null', 'null', '', ''] as const,
  byStatus: (deskId: string, status: string, sort: string) =>
    [...conversationKeys.base(deskId), status, sort, '', '', '', ''] as const,
  byAgent: (deskId: string, status: string, sort: string, agentId: string) =>
    [...conversationKeys.base(deskId), status, sort, agentId, '', '', ''] as const,
  byBrain: (deskId: string, status: string, sort: string, brainId: string) =>
    [...conversationKeys.base(deskId), status, sort, '', brainId, '', ''] as const,
  byDepartment: (deskId: string, status: string, sort: string, departmentId: string) =>
    [...conversationKeys.base(deskId), status, sort, '', '', departmentId, ''] as const,
  byView: (deskId: string, sort: string, viewId: string) =>
    [...conversationKeys.base(deskId), '', sort, '', '', '', viewId] as const,
};

export const sorts = ['newest', 'oldest'] as const;

type SortKey = (typeof sorts)[number];

type listConversationsProps = {
  deskId: string;
  sort: Sort;
  cursor: string;
  agentId: string;
  brainId: string;
  status: 'open' | 'closed' | 'spam' | 'trash';
  departmentId: string;
  viewId?: string;
};

export const API = Object.freeze({
  listConversations: async ({
    deskId,
    sort,
    cursor,
    agentId,
    brainId,
    status,
    departmentId,
    viewId,
  }: listConversationsProps): Promise<Conversations> => {
    return callGet(
      endpoints.conversations({
        id: deskId,
        sort,
        status,
        agent_id: agentId,
        department_id: departmentId,
        brain_id: brainId,
        view_id: viewId,
        cursor,
      })
    );
  },

  getConversation: async (deskId: string, sessionId: string): Promise<Conversation> =>
    callGet(endpoints.conversation(deskId, sessionId)),

  updateConversation: async (
    deskId: string,
    sessionId: string,
    data: ConversationUpdate
  ): Promise<Conversation> => callPut(endpoints.conversation(deskId, sessionId), data),

  deleteConversation: async (deskId: string, sessionId: string): Promise<Conversation> =>
    callDelete(endpoints.conversation(deskId, sessionId)),

  updateTyping: async (
    deskId: string,
    sessionId: string,
    data: { action: 'start' | 'stop'; author_type: 'agent' }
  ): Promise<Conversation> => callPatch(endpoints.typing(deskId, sessionId), data),

  updateRead: async (
    deskId: string,
    sessionId: string,
    data: { timestamp: number; author_type: 'visitor' }
  ): Promise<Conversation> => callPatch(endpoints.read(deskId, sessionId), data),
});

type ConversationPatch = SetRequired<PartialDeep<Conversation>, 'session_id' | 'desk_id'>;

type ListKey = {
  status: string;
  sort: SortKey;
  agentId: string;
  brainId: string;
  departmentId: string;
  viewId: string;
};

type WriteProps = {
  queryClient: QueryClient;
  conversation: Conversation;
  deskId: string;
  canRead: boolean;
};

const TRACKED_FIELDS: (keyof ConversationAssignee)[] = [
  'assignee_agent_id',
  'assignee_brain_id',
  'department_id',
  'status',
];

export const isFullConversation = (
  conversation: Conversation | ConversationRouting
): conversation is Conversation => 'context' in conversation || 'last_message' in conversation;

export const parseListKey = (queryKey: readonly unknown[]): ListKey | null => {
  const [, , status, sort, agentId, brainId, departmentId, viewId] = queryKey as string[];
  if (queryKey.length !== 8 || !sorts.includes(sort as SortKey)) {
    return null;
  }
  return { status, sort: sort as SortKey, agentId, brainId, departmentId, viewId };
};

const matchesId = (filter: string, value: string | null | undefined) =>
  filter === '' || (filter === 'null' ? value == null : value === filter);

/** Mirrors the filters of the conversations list endpoint for one list key. */
export const matchesList = (list: ListKey, conversation: ConversationAssignee): boolean =>
  conversation.status === (list.status || 'open') &&
  matchesId(list.agentId, conversation.assignee_agent_id) &&
  (list.brainId === 'brain'
    ? conversation.assignee_brain_id != null
    : matchesId(list.brainId, conversation.assignee_brain_id)) &&
  matchesId(list.departmentId, conversation.department_id);

// The server sets `updated` on every write, so a late event must not undo a newer cached row.
const isOlderThan = (patch: Pick<ConversationPatch, 'updated'>, cached: Conversation) =>
  !!patch.updated && isBefore(parseISO(patch.updated), parseISO(cached.updated));

/** Merges into the conversation detail when it is cached. */
export const patchDetail = (queryClient: QueryClient, conversation: ConversationPatch) => {
  queryClient.setQueryData<Conversation>(
    [endpoints.conversation(conversation.desk_id, conversation.session_id)],
    (prev) =>
      prev && (isOlderThan(conversation, prev) ? prev : mergeCopyOnWrite(prev, conversation))
  );
};

// Removed, not invalidated: a failed refetch keeps the old data on screen.
export const dropSession = (queryClient: QueryClient, deskId: string, sessionId: string) => {
  queryClient.removeQueries({ queryKey: [endpoints.conversation(deskId, sessionId)] });
  queryClient.removeQueries({ queryKey: [messagesEndpoints.messages(deskId, sessionId)] });
  queryClient.removeQueries({ queryKey: [eventsEndpoints.events(deskId, sessionId)] });
  queryClient.removeQueries({ queryKey: commentKeys.list(deskId, sessionId) });
};

/**
 * Updates one conversation inside a paginated list.
 *
 * @param prev - The previous InfiniteData<Conversations> data
 * @param sessionId - Session id of the conversation to update
 * @param mergeConv - Merger built with `createCowMerger`. Build it outside the
 * fan-out loop, or the merge runs once per key instead of once per event.
 * @return An array where the first element is the new InfiniteData<Conversations>
 * data with the conversation updated, and the second element is a boolean
 * indicating whether the session was found.
 */
export const updateConversationInPages = (
  prev: InfiniteData<Conversations> | undefined,
  sessionId: string,
  mergeConv: (conv: Conversation) => Conversation
): [InfiniteData<Conversations> | undefined, boolean] =>
  updateItemInPages<Conversations, Conversation>(
    prev,
    'conversations',
    (conv) => conv.session_id === sessionId,
    mergeConv
  );

/**
 * Flattens the loaded pages into the list, keeping the first copy of a session.
 *
 * Realtime prepends to page 0 while paging is cursor-based, so a session can sit on two pages.
 * Rows are keyed by session id, and a duplicate key drops a row and shares one height-cache entry.
 */
export const flattenConversationPages = (pages: Conversations[] | undefined): Conversation[] => {
  const seen = new Set<string>();
  return (
    pages?.flatMap((page) =>
      (page?.conversations ?? []).filter((conv) => {
        if (seen.has(conv.session_id)) {
          return false;
        }
        seen.add(conv.session_id);
        return true;
      })
    ) || []
  );
};

type ListUpdate = (prev: InfiniteData<Conversations>, list: ListKey) => InfiniteData<Conversations>;

// A fetch in flight lands rows read before the event, so the event is applied again on top.
const pendingReplays = new WeakMap<Query, ListUpdate[]>();
const replayingClients = new WeakSet<QueryClient>();

const subscribeReplays = (queryClient: QueryClient) => {
  replayingClients.add(queryClient);
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') {
      return;
    }
    const { query, action } = event;
    const updates = pendingReplays.get(query);
    const landed = action.type === 'success' && !action.manual;
    // An error keeps the data the event already wrote; a cancel reverts to it.
    if (!updates || (!landed && action.type !== 'error' && action.type !== 'setState')) {
      return;
    }
    pendingReplays.delete(query);
    const list = parseListKey(query.queryKey);
    if (landed && list) {
      queryClient.setQueryData<InfiniteData<Conversations>>(
        query.queryKey,
        (prev) => prev && updates.reduce((acc, update) => update(acc, list), prev)
      );
    }
  });
};

const replayAfterFetch = (queryClient: QueryClient, query: Query, update: ListUpdate) => {
  if (!replayingClients.has(queryClient)) {
    subscribeReplays(queryClient);
  }
  pendingReplays.set(query, [...(pendingReplays.get(query) ?? []), update]);
};

// One pass over every cached list of the desk; a list that did not change is not written.
const updateLists = (queryClient: QueryClient, deskId: string, update: ListUpdate) => {
  queryClient
    .getQueryCache()
    .findAll({ queryKey: conversationKeys.base(deskId) })
    .forEach((query) => {
      const list = parseListKey(query.queryKey);
      if (!list) {
        return;
      }
      const prev = query.state.data as InfiniteData<Conversations> | undefined;
      const next = prev && update(prev, list);
      if (next !== prev) {
        const awaitsRefetch = query.state.isInvalidated && query.state.fetchStatus !== 'fetching';
        queryClient.setQueryData(query.queryKey, next);
        // A write clears the invalidation, and the rest of the rows still need the refetch.
        if (awaitsRefetch) {
          queryClient.invalidateQueries({
            queryKey: query.queryKey,
            exact: true,
            refetchType: 'none',
          });
        }
      }
      if (query.state.fetchStatus === 'fetching') {
        replayAfterFetch(queryClient, query, update);
      }
    });
};

const withoutSession = (prev: InfiniteData<Conversations>, sessionId: string) =>
  removeItemFromPages<Conversations, Conversation>(
    prev,
    'conversations',
    (c) => c.session_id === sessionId
  );

export const removeFromLists = (queryClient: QueryClient, deskId: string, sessionId: string) => {
  updateLists(queryClient, deskId, (prev) => withoutSession(prev, sessionId));
};

export const patchInLists = (
  queryClient: QueryClient,
  deskId: string,
  patch: SetRequired<PartialDeep<Conversation>, 'session_id'>
) => {
  const mergeConv = createCowMerger<Conversation>(patch);
  updateLists(
    queryClient,
    deskId,
    (prev) => updateConversationInPages(prev, patch.session_id, mergeConv)[0]
  );
};

const isViewList = ({ queryKey }: Query) => !!parseListKey(queryKey)?.viewId;

const findViewLists = (queryClient: QueryClient, deskId: string) =>
  queryClient
    .getQueryCache()
    .findAll({ queryKey: conversationKeys.base(deskId), predicate: isViewList });

const invalidateViews = (queryClient: QueryClient, deskId: string) => {
  findViewLists(queryClient, deskId).forEach((query) => invalidateQuery(queryClient, query));
};

// One debounce per desk, so a burst on one desk does not drop the pending refresh of another.
const debouncePerDesk = (
  refresh: (queryClient: QueryClient, deskId: string) => void,
  wait: number,
  options?: DebounceSettings
) => {
  const byDesk = new Map<string, DebouncedFunc<typeof refresh>>();
  return (queryClient: QueryClient, deskId: string) => {
    const debounced = byDesk.get(deskId) ?? debounce(refresh, wait, options);
    byDesk.set(deskId, debounced);
    debounced(queryClient, deskId);
  };
};

// Views filter on the server, so their membership comes from a refetch.
const debouncedInvalidateViews = debouncePerDesk(invalidateViews, 5_000, { maxWait: 15_000 });

const findInPages = (prev: InfiniteData<Conversations>, sessionId: string) => {
  for (const page of prev.pages ?? []) {
    const found = page?.conversations?.find((c) => c.session_id === sessionId);
    if (found) {
      return found;
    }
  }
  return undefined;
};

// A conversation with no message yet sorts as the newest one.
const messageTime = ({ last_message_at }: Pick<Conversation, 'last_message_at'>) => {
  const time = Date.parse(last_message_at ?? '');
  return Number.isNaN(time) ? Infinity : time;
};

// A row that sorts past the loaded pages is left out; it arrives with the page that holds it.
const insertInOrder = (
  prev: InfiniteData<Conversations>,
  sort: SortKey,
  time: number,
  getItem: () => Conversation
): InfiniteData<Conversations> => {
  const goesBefore = (row: Conversation) =>
    sort === 'newest' ? messageTime(row) <= time : messageTime(row) > time;

  // With every loaded row removed, the place is unknown; a newest list shows it on top.
  if (sort === 'newest' && !prev.pages?.some((page) => page?.conversations?.length)) {
    return prependItemToFirstPage(prev, 'conversations', getItem());
  }

  for (const [pageIdx, page] of (prev.pages ?? []).entries()) {
    const items = page?.conversations ?? [];
    const itemIdx = items.findIndex(goesBefore);
    if (itemIdx !== -1) {
      const pages = [...prev.pages];
      pages[pageIdx] = {
        ...page,
        conversations: [...items.slice(0, itemIdx), getItem(), ...items.slice(itemIdx)],
      };
      return { ...prev, pages };
    }
  }

  const lastPage = prev.pages?.at(-1);
  return lastPage && !lastPage.pagination?.has_more
    ? appendItemToLastPage(prev, 'conversations', getItem())
    : prev;
};

// A newer event may have moved the row out of a list, so every cached copy is checked.
const hasNewerCopy = (queryClient: QueryClient, deskId: string, conversation: Conversation) => {
  const detail = queryClient.getQueryData<Conversation>([
    endpoints.conversation(deskId, conversation.session_id),
  ]);
  if (detail && isOlderThan(conversation, detail)) {
    return true;
  }
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: conversationKeys.base(deskId) })
    .some((query) => {
      const data = query.state.data as InfiniteData<Conversations> | undefined;
      const cached = data && findInPages(data, conversation.session_id);
      return !!cached && isOlderThan(conversation, cached);
    });
};

/**
 * Puts the conversation in every cached list it belongs to and takes it out of the others.
 * A view list is only updated in place and refetched later, never inserted into.
 */
export const placeInLists = ({ queryClient, conversation, deskId, canRead }: WriteProps) => {
  if (hasNewerCopy(queryClient, deskId, conversation)) {
    return;
  }
  const { session_id } = conversation;
  const mergeConv = createCowMerger<Conversation>(conversation);

  // One copy shared by every list that inserts it, so none holds the payload itself.
  let inserted: Conversation | undefined;
  const getInserted = () => (inserted ??= cloneDeep(conversation));

  updateLists(queryClient, deskId, (prev, list) => {
    const cached = findInPages(prev, session_id);
    if (cached && isOlderThan(conversation, cached)) {
      return prev;
    }
    if (!canRead) {
      return withoutSession(prev, session_id);
    }
    if (list.viewId) {
      return updateConversationInPages(prev, session_id, mergeConv)[0];
    }
    if (!matchesList(list, conversation)) {
      return withoutSession(prev, session_id);
    }
    // The lists sort by `last_message_at`, so a row whose timestamp moved must change place.
    if (
      cached &&
      (conversation.last_message_at === undefined ||
        cached.last_message_at === conversation.last_message_at)
    ) {
      return updateConversationInPages(prev, session_id, mergeConv)[0];
    }
    const next = cached ? withoutSession(prev, session_id) : prev;
    return insertInOrder(next, list.sort, messageTime(conversation), getInserted);
  });

  // Checked on the keys, not the data: a view whose first fetch is in flight needs it too.
  if (canRead && findViewLists(queryClient, deskId).length) {
    debouncedInvalidateViews(queryClient, deskId);
  }
};

const debouncedInvalidateCounts = debouncePerDesk(invalidateCounts, 5_000);

// A fetch in flight would land over the delta, and an unready scope cannot tell what the user reads.
const changeCounts = (params: Parameters<typeof applyCountDelta>[0]) => {
  const { queryClient, deskId, scope } = params;
  if (!scope.isReady || isFetchingCounts(queryClient, deskId)) {
    debouncedInvalidateCounts(queryClient, deskId);
    return;
  }
  applyCountDelta(params);
};

type CountProps = {
  queryClient: QueryClient;
  deskId: string;
  current: CountedState;
  previous?: ConversationAssignee;
  countScope: CountScope;
  isAccessLost?: boolean;
};

/**
 * Changes the counts from the state before and after the event. An older orchestrator sends no
 * `previous` or `created`, so the counts are refetched instead.
 */
const updateCounts = ({
  queryClient,
  deskId,
  current,
  previous,
  countScope,
  isAccessLost,
}: CountProps) => {
  if (!previous || !current.created) {
    debouncedInvalidateCounts(queryClient, deskId);
    return;
  }
  changeCounts({
    queryClient,
    deskId,
    before: { ...current, ...previous },
    after: current,
    isAccessLost,
    scope: countScope,
  });
};

type EventProps = WriteProps & { previous?: ConversationAssignee; countScope: CountScope };

const writeConversation = ({ previous, countScope, ...props }: EventProps) => {
  const { queryClient, conversation, deskId } = props;
  patchDetail(queryClient, conversation);
  placeInLists(props);
  updateCounts({ queryClient, deskId, current: conversation, previous, countScope });
};

export const onConversationCreated = ({ countScope, ...props }: EventProps) => {
  const { queryClient, conversation, deskId } = props;
  if (conversation.desk_id !== deskId) {
    return;
  }
  patchDetail(queryClient, conversation);
  placeInLists(props);
  changeCounts({ queryClient, deskId, before: null, after: conversation, scope: countScope });
};

// The only cache writer for an update; resolved, reopened and handover carry the same row.
export const onConversationUpdated = ({
  changedBy,
  ...props
}: EventProps & { changedBy?: string }) => {
  const { queryClient, conversation, deskId, previous } = props;
  if (conversation.desk_id !== deskId) {
    return;
  }
  writeConversation(props);

  // The server emits no socket event for tracked events; tag and user events need an agent author.
  const mayHaveNewEvents =
    !previous || !!changedBy || TRACKED_FIELDS.some((f) => previous[f] !== conversation[f]);
  if (mayHaveNewEvents) {
    queryClient.invalidateQueries({
      queryKey: [eventsEndpoints.events(deskId, conversation.session_id)],
    });
  }
};

export const onConversationExpired = (props: EventProps) => {
  if (props.conversation.desk_id !== props.deskId) {
    return;
  }
  writeConversation(props);
};

/** The user lost read access, so the payload has routing fields only: remove, never insert. */
export const onConversationAccessLost = (params: {
  queryClient: QueryClient;
  conversation: ConversationRouting;
  previous?: ConversationAssignee;
  deskId: string;
  countScope: CountScope;
}) => {
  const { queryClient, conversation, previous, deskId, countScope } = params;
  if (conversation.desk_id !== deskId) {
    return;
  }
  removeFromLists(queryClient, deskId, conversation.session_id);
  dropSession(queryClient, deskId, conversation.session_id);
  updateCounts({
    queryClient,
    deskId,
    current: conversation,
    previous,
    countScope,
    isAccessLost: true,
  });
};

/** An older orchestrator sends only the ids, without the desk or the state. */
export const onConversationRemoved = (params: {
  queryClient: QueryClient;
  conversation: ConversationId & Partial<ConversationRouting>;
  deskId: string;
  countScope: CountScope;
}) => {
  const { queryClient, conversation, deskId, countScope } = params;
  if (conversation.desk_id && conversation.desk_id !== deskId) {
    return;
  }
  removeFromLists(queryClient, deskId, conversation.session_id);
  dropSession(queryClient, deskId, conversation.session_id);
  if (conversation.status && conversation.created) {
    changeCounts({
      queryClient,
      deskId,
      before: conversation as ConversationRouting,
      after: null,
      scope: countScope,
    });
  } else {
    debouncedInvalidateCounts(queryClient, deskId);
  }
};

export const onVisitorActivity = (
  queryClient: QueryClient,
  { session_id, timestamp }: SessionActivity,
  deskId: string
) => {
  const activity = {
    session_id,
    desk_id: deskId,
    state: { last_activity_at: new Date(timestamp).toISOString() },
  };
  patchDetail(queryClient, activity);
  patchInLists(queryClient, deskId, activity);
};

type ConversationProps = { deskId: string; sessionId?: string };

type Props = ConversationProps & {
  selectedStatus?: ConversationStatusType;
  selectedAgent?: string;
  selectedDepartment?: string;
  selectedBrain?: string;
  selectedSort?: 'newest' | 'oldest';
  selectedView?: string;
};

export const conversationDetailKey = (deskId: string, sessionId?: string) => [
  endpoints.conversation(deskId, sessionId || ''),
];

/** One conversation, read-only: the detail query and its status flags. */
export const useConversationDetail = ({ deskId, sessionId }: ConversationProps) => {
  const conversationQueryKey = useMemo(
    () => conversationDetailKey(deskId, sessionId),
    [deskId, sessionId]
  );

  const { data: conversation, status: getStatus } = useQuery<Conversation, Error>({
    queryKey: conversationQueryKey,
    queryFn: () => API.getConversation(deskId, sessionId),
    enabled: !!deskId && !!sessionId,
  });

  // Computed conversation status flags
  // Note: explicitly check conversation exists first, otherwise !undefined evaluates to true
  const isExpired = Boolean(conversation && !conversation.state?.active);
  const isResolved = conversation?.status === 'closed';
  const isSpam = conversation?.status === 'spam';
  const isTrash = conversation?.status === 'trash';
  const isOpen = conversation?.status === 'open';
  const isExpiredAndResolved = isExpired && isResolved;

  return {
    context: conversation?.context,
    conversation,
    getStatus,
    isActive: conversation?.state?.active,
    isExpired,
    isExpiredAndResolved,
    isOpen,
    isResolved,
    isSpam,
    isStatusOpen: isOpen,
    isTrash,
  };
};

/** Mutations for one conversation; no detail query, so a write-only surface fetches nothing. */
export const useConversationMutations = ({ deskId, sessionId }: ConversationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const conversationQueryKey = useMemo(
    () => conversationDetailKey(deskId, sessionId),
    [deskId, sessionId]
  );

  const { mutate: updateConversation, status: updateStatus } = useMutation<
    Conversation,
    Error,
    ConversationUpdate
  >({
    mutationFn: (data) => API.updateConversation(deskId, sessionId, data),
    onSuccess: (resp, variables) => {
      patchDetail(queryClient, resp);

      // A merge can't delete keys, so a cleared (null) field would linger in cache.
      // Refetch the conversation when a user field was cleared.
      const clearedUserField =
        variables.context?.user != null &&
        Object.values(variables.context.user).some((v) => v === null);
      if (clearedUserField) {
        void queryClient.invalidateQueries({ queryKey: conversationQueryKey });
      }

      if (resp.status === 'trash') {
        placeInLists({ queryClient, conversation: resp, deskId, canRead: true });
      }
    },
    onError: (error) => {
      // Resolving is blocked while scheduled messages are pending (409) —
      // tell the agent to cancel them first instead of showing the raw
      // server message.
      if ((error as ApiError).code === 'scheduled-message-pending-on-resolve') {
        toast.error(t('scheduled_messages.pending_on_resolve'));
        // The messages may have been scheduled from another tab or by another
        // agent, so this client's cache can still say "0 pending" — which hides
        // every cancel surface. Refresh the list so the button/dot appear.
        invalidateScheduledMessages(queryClient, deskId, sessionId);
        return;
      }
      toast.error(error.message);
    },
  });

  const { mutate: deleteConversation } = useMutation<Conversation, Error, string>({
    mutationFn: (id) => API.deleteConversation(deskId, id),
    onSuccess: (resp) => {
      toast.success(t('conversation.conversation_deleted'));
      removeFromLists(queryClient, deskId, resp.session_id);
      dropSession(queryClient, deskId, resp.session_id);
    },
    meta: { errorToast: true },
  });

  const { mutate: updateTyping } = useMutation<Conversation, Error, 'start' | 'stop'>({
    mutationFn: (action) => API.updateTyping(deskId, sessionId, { action, author_type: 'agent' }),
  });

  const { mutate: updateRead } = useMutation<Conversation, Error>({
    mutationFn: () =>
      API.updateRead(deskId, sessionId, {
        timestamp: Date.now(),
        // visitor messages were read
        author_type: 'visitor',
      }),
    retry: 0,
    onSuccess: () => {
      const read = { session_id: sessionId, desk_id: deskId, agent_unread_count: 0 };
      patchDetail(queryClient, read);
      patchInLists(queryClient, deskId, read);
    },
  });

  const reopenConversation = useCallback(() => {
    updateConversation(
      { status: 'open' },
      {
        onSuccess: () => {
          toast.success(t('conversation.conv_reopened'));
        },
      }
    );
  }, [updateConversation, t]);

  return {
    updateStatus,
    deleteConversation,
    reopenConversation,
    updateConversation,
    updateRead,
    updateTyping,
  };
};

/** One conversation: detail, flags and mutations. Not `useConversations` unless you render the list. */
export const useConversation = (props: ConversationProps) => {
  const { t } = useTranslation();
  const { markAsComplete } = useHomeCheckList();
  const detail = useConversationDetail(props);
  const mutations = useConversationMutations(props);
  const { updateConversation } = mutations;

  // Here, not in `useConversationMutations`: `useHomeCheckList` reaches three more queries.
  const closeConversation = useCallback(() => {
    markAsComplete(AccountUserPrefsEnum.RESOLVE_CONVERSATION);
    updateConversation(
      { status: 'closed' },
      {
        onSuccess: () => {
          toast.success(t('conversation.conv_resolved'));
        },
      }
    );
  }, [updateConversation, markAsComplete, t]);

  return { ...detail, ...mutations, closeConversation };
};

/** The paginated conversation list, plus everything `useConversation` returns. */
export const useConversations = ({
  deskId,
  sessionId,
  selectedStatus,
  selectedSort,
  selectedAgent,
  selectedDepartment,
  selectedBrain,
  selectedView,
}: Props) => {
  // Normalize query key values to prevent unnecessary rerenders
  const normalizedStatus = selectedStatus || '';
  const normalizedSort = selectedSort || 'newest';
  const normalizedAgent = selectedAgent || '';
  const normalizedBrain = selectedBrain || '';
  const normalizedDepartment = selectedDepartment || '';
  const normalizedView = selectedView || '';

  // Memoize the query key to prevent unnecessary recreations
  const conversationsQueryKey = useMemo(
    () => [
      'conversations',
      deskId,
      normalizedStatus,
      normalizedSort,
      normalizedAgent,
      normalizedBrain,
      normalizedDepartment,
      normalizedView,
    ],
    [
      deskId,
      normalizedStatus,
      normalizedSort,
      normalizedAgent,
      normalizedBrain,
      normalizedDepartment,
      normalizedView,
    ]
  );

  const {
    data: conversations,
    status: listStatus,

    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: conversationsQueryKey,

    queryFn: async ({ pageParam }) => {
      return API.listConversations({
        deskId,
        status: (normalizedStatus as 'open' | 'closed' | 'spam' | 'trash') || 'open',
        agentId: normalizedAgent,
        departmentId: normalizedDepartment,
        brainId: normalizedBrain,
        viewId: normalizedView,
        sort: normalizedSort === 'newest' ? '-last_message_at' : 'last_message_at',
        cursor: pageParam?.cursor,
      });
    },
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor: string | undefined },
    enabled: !!deskId,
    staleTime: Infinity,
    refetchOnMount: 'always',
  });

  const unassignedCount = useMemo(
    () =>
      conversations?.pages?.[0]?.conversations?.filter(
        ({ assignee_brain_id, assignee_agent_id }) =>
          assignee_agent_id === null && assignee_brain_id === null
      ).length || 0,
    [conversations?.pages]
  );

  const flatConversations = useMemo(
    () => flattenConversationPages(conversations?.pages),
    [conversations?.pages]
  );

  const conversationState = useConversation({ deskId, sessionId });

  return {
    ...conversationState,
    conversations: flatConversations,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    listStatus,
    unassignedCount,
  };
};
